import { useState, useEffect } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import { useStore } from "../../context/StoreContext";
import {
  useGetQRConfigQuery,
  useUpdateQRConfigMutation,
} from "../../store/api";

const QRConfig = () => {
  const { selectedStoreId } = useStore();
  const {
    data: qrConfig,
    isLoading,
    isError,
    error,
  } = useGetQRConfigQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });
  const [updateQRConfig, updateState] = useUpdateQRConfigMutation();

  const [form, setForm] = useState({
    qrBaseUrl: "",
    edgePublicBaseUrl: "",
  });

  useEffect(() => {
    if (qrConfig) {
      setForm({
        qrBaseUrl: qrConfig.qrBaseUrl || "",
        edgePublicBaseUrl: qrConfig.edgePublicBaseUrl || "",
      });
    }
  }, [qrConfig]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStoreId) {
      alert("Please select a store first");
      return;
    }
    try {
      await updateQRConfig({
        storeId: selectedStoreId,
        payload: form,
      }).unwrap();
    } catch (err) {
      // error handled by UI
    }
  };

  if (!selectedStoreId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="QR Code Configuration"
          subtitle="Configure the base URL for table QR codes."
        />
        <Card>
          <Alert tone="warning">
            Please select a store to configure QR codes.
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="QR Code Configuration"
        subtitle="Set both the customer QR URL and the public Edge API URL used after QR scan."
      />

      <Card>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h3 className="mb-2 text-sm font-semibold text-slate-900">
              How QR Codes Work
            </h3>
            <p className="mb-3 text-sm text-slate-600">
              When customers scan a table QR code, they'll be directed to:
            </p>
            <code className="block rounded bg-slate-100 px-3 py-2 text-sm text-slate-800">
              {form.qrBaseUrl || "[Your Base URL]"}/table/
              {"{"}tableNo{"}"}
            </code>
            <p className="mt-3 text-xs text-slate-500">
              Example: If your base URL is "https://menu.example.com/nova-main"
              and table number is "T1", the full URL will be:
              <br />
              <span className="font-mono">
                https://menu.example.com/nova-main/table/T1
              </span>
            </p>
          </div>

          <Input
            label="QR Base URL"
            name="qrBaseUrl"
            type="url"
            placeholder="https://menu.example.com/nova-main"
            value={form.qrBaseUrl}
            onChange={handleChange}
            helper="The base URL for your customer-facing menu (without trailing slash)"
            required
          />
          <Input
            label="Edge Public Base URL"
            name="edgePublicBaseUrl"
            type="url"
            placeholder="https://store-a-edge.belinnov.co.in"
            value={form.edgePublicBaseUrl}
            onChange={handleChange}
            helper="Public Edge API base URL, must include http/https and should not include /api/qr"
            required
          />

          {isLoading && (
            <Alert tone="info">Loading current configuration...</Alert>
          )}
          {isError && (
            <Alert tone="error">
              {error?.data?.detail || "Failed to load QR configuration."}
            </Alert>
          )}
          {updateState.isSuccess && (
            <Alert tone="success">
              QR configuration updated successfully. New QR codes will use this
              URL.
            </Alert>
          )}
          {updateState.isError && (
            <Alert tone="error">
              {updateState.error?.data?.detail ||
                updateState.error?.data?.title ||
                "Failed to update QR configuration."}
            </Alert>
          )}

          <div className="flex gap-3">
            <Button type="submit" disabled={updateState.isLoading}>
              {updateState.isLoading ? "Saving..." : "Save Configuration"}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="bg-blue-50">
        <div className="flex gap-3">
          <svg
            className="h-5 w-5 flex-shrink-0 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-blue-900">
              Important Notes
            </h4>
            <ul className="mt-2 space-y-1 text-sm text-blue-800">
              <li>
                • This URL should point to your customer-facing ordering
                application
              </li>
              <li>
                • Make sure the URL is accessible to customers (not behind a
                firewall)
              </li>
              <li>
                • Changes will apply to newly generated QR codes; existing codes
                may need regeneration
              </li>
              <li>
                • Test the URL by scanning a QR code after saving to ensure it
                works correctly
              </li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default QRConfig;
